import { useState, useEffect } from 'react';
import { collection, doc, getDoc, setDoc, updateDoc, addDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { AlertTriangle, Plus, X, Upload, Save } from 'lucide-react';
import type { DisputeStatus } from '../../types';

interface DisputeManagementProps {
  orderId: string;
  onDisputeUpdate: () => void;
}

const DISPUTE_STATUSES = {
  no_action: 'Sem Ação',
  customer_dispute: 'Disputa de Cliente',
  seller_dispute: 'Disputa de Vendedor',
  delivery_delay: 'Atraso de Entrega',
  under_analysis: 'Em Análise',
  analysis_complete: 'Análise Completa'
} as const;

export default function DisputeManagement({ orderId, onDisputeUpdate }: DisputeManagementProps) {
  const [dispute, setDispute] = useState<DisputeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [newNote, setNewNote] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const { currentUser } = useAuth();

  useEffect(() => {
    loadDispute();
  }, [orderId]);

  const loadDispute = async () => {
    try {
      const disputeDoc = await getDoc(doc(db, 'disputes', orderId));
      if (disputeDoc.exists()) {
        setDispute(disputeDoc.data() as DisputeStatus);
      }
    } catch (err) {
      console.error('Error loading dispute:', err);
      setError('Erro ao carregar dados da disputa');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: DisputeStatus['status']) => {
    if (!currentUser) return;

    try {
      setSaving(true);
      const timestamp = new Date().toISOString();

      if (!dispute) {
        // Create new dispute
        const disputeData: DisputeStatus = {
          id: orderId,
          orderId,
          status: newStatus,
          notes: [],
          createdAt: timestamp,
          updatedAt: timestamp,
          createdBy: currentUser.uid,
          createdByName: currentUser.displayName || currentUser.email || 'Admin'
        };

        await setDoc(doc(db, 'disputes', orderId), disputeData);
        setDispute(disputeData);

        // Add public comment to order
        const orderRef = doc(db, 'orders', orderId);
        const orderDoc = await getDoc(orderRef);
        if (orderDoc.exists()) {
          const orderData = orderDoc.data();
          const comments = orderData.comments || [];
          comments.push({
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            text: 'Foi iniciada o tratamento de uma reclamação sobre esta encomenda',
            createdAt: timestamp,
            createdBy: 'LOGNOW',
            isSystem: true
          });
          await updateDoc(orderRef, { comments });
        }
      } else {
        // Update existing dispute
        const updateData: Partial<DisputeStatus> = {
          status: newStatus,
          updatedAt: timestamp
        };

        await updateDoc(doc(db, 'disputes', orderId), updateData);
        setDispute({ ...dispute, ...updateData });

        // If status is analysis_complete, add completion comment
        if (newStatus === 'analysis_complete') {
          const orderRef = doc(db, 'orders', orderId);
          const orderDoc = await getDoc(orderRef);
          if (orderDoc.exists()) {
            const orderData = orderDoc.data();
            const comments = orderData.comments || [];
            comments.push({
              id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              text: 'A reclamação sobre esta encomenda foi finalizada com sucesso',
              createdAt: timestamp,
              createdBy: 'LOGNOW',
              isSystem: true
            });
            await updateDoc(orderRef, { comments });
          }
        }
      }

      onDisputeUpdate();
    } catch (err) {
      console.error('Error updating dispute status:', err);
      setError('Erro ao atualizar estado da disputa');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNote = async () => {
    if (!currentUser || !newNote.trim()) return;

    try {
      setSaving(true);
      const timestamp = new Date().toISOString();

      // Convert attachments to base64
      const base64Attachments = await Promise.all(
        attachments.map(file => new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        }))
      );

      const note = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        text: newNote.trim(),
        attachments: base64Attachments,
        createdAt: timestamp,
        createdBy: currentUser.uid,
        createdByName: currentUser.displayName || currentUser.email || 'Admin'
      };

      // If no dispute exists yet, create it first
      if (!dispute) {
        const disputeData: DisputeStatus = {
          id: orderId,
          orderId,
          status: 'under_analysis',
          notes: [note],
          createdAt: timestamp,
          updatedAt: timestamp,
          createdBy: currentUser.uid,
          createdByName: currentUser.displayName || currentUser.email || 'Admin'
        };

        await setDoc(doc(db, 'disputes', orderId), disputeData);
        setDispute(disputeData);
      } else {
        const updatedDispute = {
          ...dispute,
          notes: [...dispute.notes, note],
          updatedAt: timestamp
        };

        await updateDoc(doc(db, 'disputes', orderId), updatedDispute);
        setDispute(updatedDispute);
      }

      setNewNote('');
      setAttachments([]);
      setError('');
    } catch (err) {
      console.error('Error adding note:', err);
      setError('Erro ao adicionar nota');
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = (files: FileList) => {
    const newFiles = Array.from(files).filter(file => 
      file.type.startsWith('image/') || file.type === 'application/pdf'
    );

    if (newFiles.length !== files.length) {
      setError('Apenas imagens e PDFs são permitidos');
      return;
    }

    const totalSize = newFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > 5 * 1024 * 1024) { // 5MB limit
      setError('Tamanho total dos arquivos não pode exceder 5MB');
      return;
    }

    setAttachments([...attachments, ...newFiles]);
    setError('');
  };

  if (loading) {
    return (
      <div className="flex justify-center py-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 bg-white p-6 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <AlertTriangle className="text-yellow-500" />
          Gestão de Reclamação
        </h3>
        <select
          value={dispute?.status || 'no_action'}
          onChange={(e) => handleStatusChange(e.target.value as DisputeStatus['status'])}
          className="rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
        >
          {Object.entries(DISPUTE_STATUSES).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {error && (
        <div className="text-red-600 text-sm">
          {error}
        </div>
      )}

      {/* Notes Section */}
      <div className="space-y-4">
        {dispute?.notes.map((note) => (
          <div key={note.id} className="bg-gray-50 p-4 rounded-lg">
            <div className="flex justify-between mb-2">
              <span className="font-medium">{note.createdByName}</span>
              <span className="text-sm text-gray-500">
                {new Date(note.createdAt).toLocaleString('pt-PT')}
              </span>
            </div>
            <p className="text-gray-700 whitespace-pre-wrap">{note.text}</p>
            {note.attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {note.attachments.map((attachment, index) => (
                  <a
                    key={index}
                    href={attachment}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:text-primary-dark text-sm"
                  >
                    Anexo {index + 1}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}

        {/* Add Note Form */}
        <div className="space-y-4">
          <textarea
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            placeholder="Adicionar nota interna..."
            rows={4}
            className="w-full rounded-md border-gray-300 shadow-sm focus:border-primary focus:ring-primary"
          />

          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 cursor-pointer">
              <Upload size={20} />
              <span>Anexar Arquivo</span>
              <input
                type="file"
                multiple
                accept="image/*,.pdf"
                className="hidden"
                onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
              />
            </label>

            {attachments.length > 0 && (
              <div className="text-sm text-gray-600">
                {attachments.length} arquivo(s) selecionado(s)
              </div>
            )}
          </div>

          <div className="flex justify-end">
            <button
              onClick={handleAddNote}
              disabled={saving || !newNote.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-secondary rounded-md hover:bg-primary-dark disabled:opacity-50"
            >
              <Save size={20} />
              {saving ? 'Salvando...' : 'Salvar Nota'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}